<script setup lang="ts">
import { computed } from 'vue'

import ParagraphTypeImage from './ParagraphTypeImage.vue'
import { useConfigStore } from '../../stores/config'

import type { FileDownloadParagraph } from '../../utils/paragraph-schema/paragraphs'
import type { Brand } from '@xcel-next/core/enums'

const props = defineProps<{
  fileDownload: FileDownloadParagraph['fileDownload']
  brand?: Brand
}>()

const configStore = useConfigStore()

// Keep cached composed payloads usable until their invalidation is guaranteed.
const fileId = computed(() => props.fileDownload?.fileId ?? props.fileDownload?.id)

const formattedFileSize = computed(() => {
  const bytes = props.fileDownload?.fileSize
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const decimals = 2
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes ?? 0) / Math.log(k))
  return parseFloat(((bytes ?? 0) / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i]
})

const downloadLabel = computed(() => `${props.fileDownload?.downloadButtonLabel || 'Download'} (${formattedFileSize.value})`)

const downloadUrl = computed(() => {
  const fileServiceUrl = configStore.rsConfig.fileServiceUrl
  const url = `${fileServiceUrl}/files/${props.brand}/${fileId.value}/${encodeURIComponent(props.fileDownload?.originalName ?? '')}`
  const assetId = props.fileDownload?.assetId
  return assetId ? `${url}?${new URLSearchParams({ assetId })}` : url
})
</script>

<template>
  <div class="bx-content bx-content__continuous-text bx-typo--content-continuous-text bx-download bx-typo--download">
    <div class="bx-download__inner-wrapper">
      <span v-if="fileDownload?.description"
            v-html="fileDownload?.description" />
      <a :href="downloadUrl"
         :download="downloadUrl"
         :title="downloadLabel"
         target="_blank"
         class="bx-button">
        <svg class="icon icon-download">
          <use href="#download" />
        </svg>
        {{ downloadLabel }}
      </a>
    </div>
    <div class="bx-download__image">
      <ParagraphTypeImage v-if="fileDownload?.previewImage"
                          :image="fileDownload?.previewImage"
                          image-src-set-name="downloadPreviewImage"
                          :brand="brand" />
    </div>
  </div>
</template>
